<script setup lang="ts">
import { createRange, keyValues } from "../../utils/helpers";
const props = defineProps({
  start: {
    type: [Number, String],
    default: 1,
  },
  modelValue: {
    type: Number,
    default: (props: any) => props.start,
  },
  disabled: Boolean,
  length: {
    type: [Number, String],
    default: 1,
    validator: (val: number) => val % 1 === 0,
  },
  totalVisible: {
    type: [Number, String],
    default: 7,
  },
  prevIcon: {
    type: String,
    default: "fa-solid fa-forward-step",
  },
  firstIcon: {
    type: String,
    default: "fa-solid fa-forward-fast",
  },
  lastIcon: {
    type: String,
    default: "fa-solid fa-backward-fast",
  },
  nextIcon: {
    type: String,
    default: "fa-solid fa-backward-step",
  },
  position: {
    type: String,
    default: "center",
  },
  ellipsis: {
    type: String,
    default: "...",
  },
  showFirstLastPage: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  "moreLoad",
  {
    "update:modelValue": (value: number) => true,
    first: (value: number) => true,
    prev: (value: number) => true,
    next: (value: number) => true,
    last: (value: number) => true,
  },
]);

const page = useProxiedModel(props, "modelValue") as Ref<number>;
const more = useProxiedModel(props, "more") as Ref<number>;

const maxButtons = ref(-1);

const _position = computed(() => {
  switch (props.position) {
    case "center":
      return "justify-center";
    case "start":
      return "justify-start";
    case "end":
      return "justify-end";
  }
});

const length = computed(() => parseInt(props.length as string, 10));
const start = computed(() => parseInt(props.start as string, 10));

const totalVisible = computed(() => {
  if (props.totalVisible)
    return Math.min(
      parseInt((props.totalVisible as string) ?? "", 10),
      length.value
    );
  else if (maxButtons.value >= 0) return maxButtons.value;
  return length.value;
});

const range = computed(() => {
  if (length.value <= 1) return [];

  if (totalVisible.value < 2) return [page.value];

  if (length.value <= totalVisible.value) {
    return createRange(length.value, start.value);
  }

  const even = totalVisible.value % 2 === 0;
  const middle = even
    ? totalVisible.value / 2
    : Math.floor(totalVisible.value / 2);
  const left = even ? middle : middle + 1;
  const right = length.value - middle;

  if (left - page.value >= 0) {
    return [
      ...createRange(Math.max(1, totalVisible.value - 1), start.value),
      props.ellipsis,
      length.value,
    ];
  } else if (page.value - right >= 0) {
    const rangeLength = totalVisible.value - 1;
    const rangeStart = length.value - rangeLength + start.value;
    return [
      start.value,
      props.ellipsis,
      ...createRange(rangeLength, rangeStart),
    ];
  } else {
    const rangeLength = Math.max(1, totalVisible.value - 3);
    const rangeStart =
      rangeLength === 1
        ? page.value
        : page.value - Math.ceil(rangeLength / 2) + start.value;
    return [
      start.value,
      props.ellipsis,
      ...createRange(rangeLength, rangeStart),
      props.ellipsis,
      length.value,
    ];
  }
});

// TODO: 'first' | 'prev' | 'next' | 'last' does not work here?
function setValue(e: Event, value: number, event?: any) {
  page.value = value;
  event && emit(event, value);
}

function addValue() {
  emit("moreLoad");
}

const { refs, updateRef } = toRefs<ComponentPublicInstance>();

const items = computed(() => {
  return range.value.map((item, index) => {
    const ref = (e: any) => updateRef(e, index);

    if (typeof item === "string") {
      return {
        isActive: false,
        page: item,
        props: {
          ref,
          ellipsis: true,
          icon: true,
          disabled: true,
        },
      };
    } else {
      const isActive = item === page.value;
      return {
        isActive,
        page: item,
        props: {
          ref,
          ellipsis: false,
          icon: true,
          disabled: !!props.disabled || props.length < 2,
          onClick: (e: Event) => setValue(e, item),
        },
      };
    }
  });
});

const controls = computed(() => {
  const moreHidden =
    !!props.disabled || page.value >= start.value + length.value - 1;
  const prevDisabled = !!props.disabled || page.value <= start.value;
  const nextDisabled =
    !!props.disabled || page.value >= start.value + length.value - 1;

  return {
    moreLoad: {
      onClick: (e: Event) => addValue(),
      disabled: moreHidden,
    },
    first: props.showFirstLastPage
      ? {
          icon: props.lastIcon,
          onClick: (e: Event) => setValue(e, start.value, "first"),
          disabled: prevDisabled,
        }
      : undefined,
    prev: {
      icon: props.nextIcon,
      onClick: (e: Event) => setValue(e, page.value - 1, "prev"),
      disabled: prevDisabled,
    },
    next: {
      icon: props.prevIcon,
      onClick: (e: Event) => setValue(e, page.value + 1, "next"),
      disabled: nextDisabled,
    },
    last: props.showFirstLastPage
      ? {
          icon: props.firstIcon,
          onClick: (e: Event) =>
            setValue(e, start.value + length.value - 1, "last"),
          disabled: nextDisabled,
        }
      : undefined,
  };
});

function updateFocus() {
  const currentIndex = page.value - start.value;
  refs.value[currentIndex]?.$el.focus();
}

function onKeydown(e: KeyboardEvent) {
  if (e.key === keyValues.left && !props.disabled && page.value > props.start) {
    page.value = page.value - 1;
    nextTick(updateFocus);
  } else if (
    e.key === keyValues.right &&
    !props.disabled &&
    page.value < start.value + length.value - 1
  ) {
    page.value = page.value + 1;
    nextTick(updateFocus);
  }
}
</script>

<template>
  <nav role="navigation" @onKeydown="onKeydown" data-test="v-pagination-root">
    <section class="flex flex-col justify-center items-center gap-4">
      <p-button
        class="w-30 flex justify-center"
        v-if="!controls.moreLoad.disabled"
        type="tertiary-secondary"
        fontSize="lg"
        @click="controls.moreLoad.onClick"
        >Показать еще</p-button
      >
      <ul class="inline-flex list-none w-full gap-2" :class="_position">
        <li v-if="showFirstLastPage">
          <p-pagination-btn
            type="third"
            @click="controls.first.onClick"
            :disabled="controls.first.disabled"
          >
            <p-icon-pagination-arrow
              class="w-4 h-4"
              :isDisable="controls.first.disabled"
            ></p-icon-pagination-arrow>
          </p-pagination-btn>
        </li>

        <li>
          <p-pagination-btn
            type="third"
            @click="controls.prev.onClick"
            :disabled="controls.prev.disabled"
          >
            <p-icon-pagination-arrow
              :isDisable="controls.prev.disabled"
              class="w-4 h-4"
            ></p-icon-pagination-arrow>
          </p-pagination-btn>
        </li>

        <li v-for="item in items">
          <p-pagination-btn
            :type="item.isActive ? 'primary' : 'secondary'"
            :value="item.page"
            :disabled="item.props.disabled"
            @click="item.props?.onClick"
            >{{ item.page }}</p-pagination-btn
          >
        </li>

        <li>
          <p-pagination-btn
            type="third"
            @click="controls.next.onClick"
            :disabled="controls.next.disabled"
          >
            <p-icon-pagination-arrow
              :isDisable="controls.next.disabled"
              class="w-4 h-4 rotate-180"
            ></p-icon-pagination-arrow>
          </p-pagination-btn>
        </li>

        <li v-if="showFirstLastPage">
          <p-pagination-btn
            type="third"
            @click="controls.last.onClick"
            :disabled="controls.last.disabled"
          >
            <p-icon-pagination-arrow
              :isDisable="controls.last.disabled"
              class="w-4 h-4 rotate-180"
            ></p-icon-pagination-arrow>
          </p-pagination-btn>
        </li>
      </ul>
    </section>
  </nav>
</template>
